import { Pagination, PagingResponseModel } from 'blg-akaun-ts-lib';
import { EMPTY, Observable } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

export const gridOptionsServerSide = {
    serverSideStoreType: 'partial',
    rowModelType: 'serverSide',
    defaultColDef: {
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {suppressFilterButton: true},
        minWidth: 200,
        flex: 2,
        sortable: true,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        suppressCsvExport: true
    },
    pagination: true,
    animateRows: true,
    sideBar: 'columns'
};
export const gridOptionsClientSide = {
    rowModelType: 'clientSide',
    defaultColDef: {
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {suppressFilterButton: true},
        minWidth: 200,
        flex: 2,
        sortable: true,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        suppressCsvExport: true
    },
    pagination: true,
    animateRows: true,
    sideBar: 'columns'
};

/**
 * Decides whether the data is being filtered based on the filterModel
 * provided by the AG grid request and returns what contains an object
 * that contains a function which takes in a view model and decides if
 * it contains the filter search string and a boolean whether the data is
 * currently being filtered or not.
 *
 * @param filterModel : the filter model provided by AG grid
 * @returns {by: a function, isFiltering: true if not currently filtered, false otherwise}
 */
export function pageFiltering(filterModel) {
    const noFilters = Object.keys(filterModel).length <= 0;
    if (noFilters) {
        return {
            by : (viewModel) => true,
            isFiltering : noFilters
        };
    }
    return {
        by: (viewModel) =>  Object
        .keys(filterModel)
        .map(col => {
            const fields = col.split('.');
            const val = fields.reduce(((acc, property) => acc ? acc[property] : ''), viewModel);

            return filterModel.filterType === 'number' ?
            val === filterModel[col].filter : val?.toLowerCase().includes(filterModel[col].filter.toLowerCase());
        })
        .reduce((p, c) => p && c),
        isFiltering: noFilters
    };
}

/**
 * Sorts data according to the sort model provided by AG grid and returns
 * this new data.
 *
 * @param sortModel : the sort model provided by AG grid
 * @returns the sorted data
 */
export function pageSorting(sortModel) {
    return (data) => {
        // return data if there is no sorting required
        if (sortModel.length <= 0) {
            return data;
        }
        // make a copy of data
        let newData = data.map(o => o);
        sortModel.forEach(model => {
        const col = model.colId;
        const fields = col.split('.');
        const getVal = (entity) => fields.reduce((acc, property) => acc ? acc[property] : null, entity);

        // sort the data
        newData = model.sort === 'asc' ?
            // p and c are of type VendorContainerModel
            newData.sort((p, c) => 0 - (getVal(p) > getVal(c) ? -1 : 1)) :
            newData.sort((p, c) => 0 - (getVal(p) > getVal(c) ? 1 : -1));
        });

        return newData;
    };
}

/**
   * Set datasource on the AG grid to empty.
   */
export function serverSideClear(gridApi) {
    const dataSource = {
      getRows: grid => {
        grid.successCallback([], 0);
      }
    };

    gridApi.setServerSideDatasource(dataSource);
}

/**
 * Pages through every record matching `criteria` and returns them as one array.
 *
 * Criteria-based endpoints apply a `limit`, so a single getByCriteria call only
 * ever returns one page. Flows that used to resolve an unbounded guid list
 * through SubQueryService (permission-set deletion, role deletion, removing
 * permission sets from a role) must not silently stop at the first page: a
 * permission set with more assignee-target links than the page size would leave
 * residual links behind.
 *
 * `fetchPage` receives the Pagination to use and returns the service call, e.g.
 *
 *   fetchAllByCriteria(
 *     pagination => this.someService.getByCriteria(pagination, this.apiVisa),
 *     [{ columnName: 'perm_set_hdr_guid', operator: '=', value: guid }]
 *   )
 *
 * `maxPages` is a runaway guard, not a business limit: it only trips if the
 * endpoint ignores `offset` and keeps returning full pages. It is deliberately
 * high enough (200 x 500 = 100,000 records) that no real data set reaches it.
 */
export function fetchAllByCriteria<T>(
    fetchPage: (pagination: Pagination) => Observable<PagingResponseModel<T>>,
    criteria: any[],
    pageSize: number = 500,
    maxPages: number = 200
): Observable<T[]> {
    const page = (offset: number) => fetchPage(new Pagination(offset, pageSize, criteria));

    return page(0).pipe(
        expand((response, index) => {
            const received = (response && response.data) ? response.data.length : 0;
            const isLastPage = received < pageSize;
            const hitGuard = index + 1 >= maxPages;
            if (hitGuard && !isLastPage) {
                console.warn(
                    `fetchAllByCriteria: stopped after ${maxPages} pages of ${pageSize}. `
                    + 'Some matching records were not read.'
                );
            }
            return (isLastPage || hitGuard) ? EMPTY : page((index + 1) * pageSize);
        }),
        reduce((all: T[], response) => all.concat((response && response.data) ? response.data : []), [])
    );
}
